import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Loan-standing uploads create an Observatory document, then an employee-plan-account-documents
 * join so reflection can evaluate and broadcast the EPA verdict. Name-match PATCHes also trigger
 * server-side reevaluation.
 */

const employeePlanAccountUrl = (path: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'employee-plan-accounts' },
    op: 'query',
    resourcePath: 'employee-plan-accounts',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/**
 * POST /employee-plan-account-documents — link an uploaded ObservableDocument
 * to the EmployeePlanAccount it was submitted to verify. Created right after
 * the document create; consumed on reflection (the direct-reference
 * evaluation) and at verdict delivery.
 *
 * Member names and types are CAMELIZED, not dasherized: the handler chain
 * sends `x-json-api-key-format: camelized`, and tio-api honors that header
 * for request-body parsing too — dasherized keys are silently dropped.
 */
export function createEmployeePlanAccountDocumentRequest(
  employeePlanAccountId: string,
  observableDocumentId: string
): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'employee-plan-account-documents' },
    op: 'query',
    resourcePath: 'employee-plan-account-documents',
  };

  return {
    url: buildBaseURL(opts),
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'employeePlanAccountDocuments',
        relationships: {
          employeePlanAccount: {
            data: { type: 'employeePlanAccounts', id: employeePlanAccountId },
          },
          observableDocument: {
            data: { type: 'observableDocuments', id: observableDocumentId },
          },
        },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Participant-settable answers exclude pending. responsible affirms financial responsibility
 * without asserting a name match.
 */
export type SlrNameMatchConfirmation = 'confirmed' | 'responsible' | 'denied';

/**
 * PATCH /employee-plan-accounts/:id — record the participant's answer to the
 * name-match prompt (`good_standing_reason ==
 * 'name_match_confirmation_required'`). Setting `name_match_confirmation`
 * triggers an inline server-side re-evaluation: `confirmed` and `responsible`
 * let the name check pass; `denied` resolves to ineligible/name_mismatch.
 * Note the PATCH response is rendered from the pre-evaluation record —
 * callers should refetch for the settled state.
 *
 * Member names are CAMELIZED, not dasherized: the handler chain sends
 * `x-json-api-key-format: camelized`, and tio-api honors that header for
 * request-body parsing too — a dasherized `name-match-confirmation` is
 * silently dropped as "Param not allowed" (200 + meta warning, no-op).
 */
export function confirmSlrLoanStandingNameMatchRequest(
  employeePlanAccountId: string,
  confirmation: SlrNameMatchConfirmation
): RequestInfo {
  return {
    url: employeePlanAccountUrl(employeePlanAccountId),
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'employeePlanAccounts',
        id: employeePlanAccountId,
        attributes: { nameMatchConfirmation: confirmation },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}
